import React, { useCallback, useMemo } from 'react';
import { Box, Button, CircularProgress, List, ListItem, Tooltip } from '@mui/material';
import { jsonForm } from '@europrocurement/flexy-form/utils/types/formTypes';
import {
    AccordionJsonFormsFiltered,
    type DossierFormulaire,
    useDossierFormulaireContext,
    useCurrentDossierCtx,
    DOSSIER_STATUS_DRAFT,
    DOSSIER_STATUS_AWAITING,
    DOSSIER_STATUS_IN_PROGRESS,
    DOSSIER_STATUS_INCOMPLETE,
    DOSSIER_STATUS_TO_CHECK,
} from '@europrocurement/l2d-modules';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { useSnackbar } from 'notistack';
import _ from 'lodash';

const PREPARATION_STATUSES = [
    DOSSIER_STATUS_DRAFT,
    DOSSIER_STATUS_AWAITING,
    DOSSIER_STATUS_IN_PROGRESS,
    DOSSIER_STATUS_INCOMPLETE,
    DOSSIER_STATUS_TO_CHECK,
];

const createObjectFromForm = (formConfig: jsonForm): Record<string, unknown> => {
    const result: Record<string, unknown> = {};

    (formConfig?.pages || []).forEach((page) => {
        (page.sections || [])
            .filter((section) => 'defaultvalue' in section && section.name)
            .forEach((section) => {
                const path = section.name.split('.');
                let current = result;

                path.forEach((key, index) => {
                    if (index === path.length - 1) {
                        (current as Record<string, unknown>)[key] = section.defaultvalue;
                    } else {
                        if (!current[key]) {
                            current[key] = {};
                        }
                        current = current[key] as Record<string, unknown>;
                    }
                });
            });
    });

    return result;
};

const iconSx = {
    display: 'flex',
    alignItems: 'center',
    width: 'auto',
    fontSize: '10px',
    lineHeight: '0',
};

const FormulaireTab: React.FunctionComponent = function () {
    const { dossier } = useCurrentDossierCtx();
    const {
        dataLoaded,
        formulaires,
        currentPrestationId,
        setCurrentPrestationId,
        getCurrentFormulaire,
        updateFormulaire,
        persistAllFormulaires,
        hasNonPersistedChanges,
    } = useDossierFormulaireContext();
    const { enqueueSnackbar } = useSnackbar();

    const formulaire = getCurrentFormulaire();
    const isEditable = PREPARATION_STATUSES.includes(dossier?.statutId as number);

    const defaultValues = useMemo(
        () =>
            formulaire?.formulaireJson
                ? (_.merge(
                      {},
                      createObjectFromForm(formulaire.formulaireJson as jsonForm),
                      formulaire.prestationJson,
                  ) as Record<string, unknown>)
                : {},
        [formulaire],
    );

    const handleChange = useCallback(
        (data: Record<string, unknown>) => {
            if (formulaire) {
                updateFormulaire({
                    ...formulaire,
                    prestationJson: _.merge({}, formulaire.prestationJson, data),
                });
            }
        },
        [formulaire, updateFormulaire],
    );

    const handleSave = useCallback(async () => {
        try {
            await persistAllFormulaires();
            enqueueSnackbar('Formulaire enregistré avec succès');
        } catch (err) {
            console.error(err);
            enqueueSnackbar("Une erreur est survenue lors de l'enregistrement", {
                variant: 'error',
            });
        }
    }, [persistAllFormulaires, enqueueSnackbar]);

    if (!dossier?.id || !dataLoaded) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '2em' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!formulaires.length) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
            {formulaires.length > 1 && (
                <List
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '0 20px',
                        padding: 0,
                    }}
                >
                    {formulaires.map((form: DossierFormulaire) => {
                        const isCurrent = form.prestationId === currentPrestationId;
                        return (
                            <ListItem
                                key={form.prestationId}
                                sx={{ width: 'auto', padding: 0 }}
                            >
                                <Tooltip
                                    title={form.label}
                                    arrow
                                    placement="top"
                                >
                                    <Button
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0 5px',
                                            fontSize: '13px',
                                            color: isCurrent ? 'primary.main' : 'text.secondary',
                                            ':hover': { color: 'primary.main' },
                                        }}
                                        onClick={() => setCurrentPrestationId(form.prestationId)}
                                    >
                                        {form.complete && (
                                            <FaOptionIcon
                                                icon={['fasdl', 'check']}
                                                sx={iconSx}
                                            />
                                        )}
                                        {form.label.length > 30
                                            ? `${form.label.substring(0, 27)}[...]`
                                            : form.label}
                                    </Button>
                                </Tooltip>
                            </ListItem>
                        );
                    })}
                </List>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {!!formulaire?.formulaireJson && (
                    <Box sx={isEditable ? {} : { pointerEvents: 'none', opacity: 0.85 }}>
                        <AccordionJsonFormsFiltered
                            json={formulaire.formulaireJson as jsonForm}
                            defaultValues={defaultValues}
                            formulaireId={formulaire.formulaireId as number}
                            formaliteId={formulaire.formaliteId}
                            visibility={formulaire.formulaireVisibilite || 'PARTIAL'}
                            expanded
                            onChange={isEditable ? handleChange : () => {}}
                        />
                    </Box>
                )}

                {isEditable && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            paddingBottom: '16px',
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!hasNonPersistedChanges}
                        >
                            Enregistrer
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default FormulaireTab;
